import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, Stack, Tab, Tabs, Typography } from "@mui/material";

import { ErrorState, KernelLockup, Loading, Permission, SchemeToggle } from "@lopie/kernel-ui";

import ConfirmDialog from "./components/ConfirmDialog.jsx";
import DrillDialog from "./components/DrillDialog.jsx";
import HistoryDialog from "./components/HistoryDialog.jsx";
import AlertDialog from "./components/AlertDialog.jsx";
import ScheduleDialog from "./components/ScheduleDialog.jsx";
import ShareDialog from "./components/ShareDialog.jsx";
import { saveDashboards, loadDashboards } from "./lib/dashboards.js";
import { offered } from "./lib/declared.js";
import { dashboardBody, dashboardFrom, failureText, movable, reportBody, savedFrom, scheduleLine } from "./lib/reports.js";
import { useKernel } from "./lib/run.js";
import { deleteReport, loadSaved, newState, stateFromSpec, typesOf } from "./lib/spec.js";
import Build from "./screens/Build.jsx";
import Dashboards from "./screens/Dashboards.jsx";
import Overview from "./screens/Overview.jsx";
import Saved, { figureOf } from "./screens/Saved.jsx";

/**
 * Charts over whatever a connector brought in, counting only what the person
 * signed in can open.
 *
 * ## One idea, said where it is used
 *
 * Every figure here is a question asked of the kernel as the person. It is not
 * a report somebody prepared and everybody reads: two people open the same page
 * and see different numbers, because they may open different records. The
 * permission strip says which tags decided it, each chart says what it counts,
 * and nothing says that anything was left out, because that would tell a person
 * that records exist which they may not see.
 *
 * ## Rendered from the kernel
 *
 * The types, the fields, what each field may be used for and the operators all
 * come from `GET /schema`. There is no list of any of them here, so a type
 * added to the kernel is in the builder and on the overview on the next load.
 */
export default function App({ token, claims, onSignOut }) {
  const { get, query, drill, reports: api, ask } = useKernel(token);
  const [schema, setSchema] = useState(null);
  const [sources, setSources] = useState([]);
  const [failure, setFailure] = useState(null);
  const [tab, setTab] = useState("overview");
  const [state, setState] = useState(null);
  const [runNonce, setRunNonce] = useState(0);
  const [permitted, setPermitted] = useState(null);

  const owner = claims?.username || claims?.sub || "anonymous";
  // What the kernel holds for this person: theirs, and what was shared with them.
  const [list, setList] = useState(null);
  const [listFailed, setListFailed] = useState(false);
  // What is still only in this browser, from before reports lived in the kernel.
  const [local, setLocal] = useState(() => movable(loadSaved(owner), loadDashboards(owner)));
  const [moving, setMoving] = useState(false);
  const [dashboardId, setDashboardId] = useState("");
  const [drilling, setDrilling] = useState(null);
  const [sharing, setSharing] = useState(null);
  const [versioning, setVersioning] = useState(null);
  const [emailing, setEmailing] = useState(null);
  const [alerting, setAlerting] = useState(null);
  const [removing, setRemoving] = useState(null);
  // The saved report the builder was opened from, when it is the person's own: what Update changes.
  const [editing, setEditing] = useState(null);
  const [notice, setNotice] = useState("");
  // What connectors suggest, read once, and how many records of each type this person can open.
  const [suggested, setSuggested] = useState(null);
  const [counts, setCounts] = useState(null);
  // The newest version of each report this page has seen, so two edits in a row do not
  // trip over each other: the second names the version the first made.
  const versions = useRef({});
  // The newest copy of each dashboard this page knows of: what an edit is made against, because
  // the copy on screen can be a round trip behind two quick edits.
  const latest = useRef({});
  const queue = useRef(Promise.resolve());

  const refresh = useCallback(async () => {
    try {
      const out = await api.list();
      for (const r of out.reports || []) {
        versions.current[r.id] = r.version;
        if (r.kind === "dashboard") latest.current[r.id] = dashboardFrom(r);
      }
      setList(out.reports || []);
      setListFailed(false);
    } catch {
      setListFailed(true);
      setList((current) => current || []);
    }
  }, [api]);

  useEffect(() => {
    let live = true;
    get("/schema").then((s) => {
      if (!live) return;
      setSchema(s);
      setState(newState(typesOf(s)[0]?.type || ""));
    }).catch((e) => live && setFailure(e));
    get("/insights/dashboards")
      .then((out) => live && setSuggested(out.dashboards || []))
      .catch(() => live && setSuggested(false));
    // The connectors a report may be limited to. Without them the form simply does not offer the choice.
    get("/insights/sources")
      .then((out) => live && setSources((out.sources || []).map((s) => s.name)))
      .catch(() => live && setSources([]));
    refresh();
    return () => { live = false; };
  }, [get, refresh]);

  const groups = useMemo(() => {
    const raw = claims?.["cognito:groups"];
    return Array.isArray(raw) ? raw : String(raw || "").replace(/[[\]]/g, "").split(/[ ,]+/).filter(Boolean);
  }, [claims]);

  const saved = useMemo(() => (list || []).filter((r) => r.kind === "report").map(savedFrom), [list]);
  const dashboards = useMemo(() => (list || []).filter((r) => r.kind === "dashboard").map(dashboardFrom), [list]);
  // Open on the first dashboard the person made, as the page always did, once the kernel has said which.
  useEffect(() => {
    if (list && !dashboardId && dashboards.length) setDashboardId(dashboards[0].id);
  }, [list, dashboards, dashboardId]);
  // A link in a report email: `#report=<id>`. Opened once the list is read, and never by guessing: an id
  // the person cannot open is told the same thing as one that does not exist.
  const linked = useRef(false);
  useEffect(() => {
    if (linked.current || !list || !schema || !state) return;
    linked.current = true;
    const m = /^#report=([A-Za-z0-9]+)$/.exec(window.location.hash || "");
    if (!m) return;
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    const hit = list.find((r) => r.id === m[1]);
    if (!hit) { setNotice("That link is to a report you cannot open, or one that no longer exists."); return; }
    if (hit.kind === "dashboard") { setDashboardId(hit.id); setTab("dashboards"); return; }
    const found = savedFrom(hit);
    setState(found.state); setEditing(found.role === "owner" ? found : null); setTab("build"); setRunNonce((n) => n + 1);
  }, [list, schema, state]);
  const onAnswer = useCallback((result) => setPermitted(result.permitted_tags || []), []);
  const declared = useMemo(() => offered(suggested || [], counts), [suggested, counts]);

  // A question in words: the spec the model proposed opens in the form, is run, and is checked by a person.
  const askInWords = async (question) => {
    const got = await ask(question);
    if (!got.ok) throw got.error;
    const r = got.result;
    if (r.answer !== "spec") return { kind: "refused", reason: r.reason, remaining: r.remaining, perDay: r.per_day };
    const { state: next, leftOut } = stateFromSpec(r.spec, schema);
    setState(next); setEditing(null); setRunNonce((n) => n + 1);
    return { kind: "spec", says: r.says, leftOut, remaining: r.remaining, perDay: r.per_day };
  };
  const openType = (type) => { setState(newState(type)); setEditing(null); setTab("build"); setRunNonce((n) => n + 1); };
  const openSaved = (r) => { setState(r.state); setEditing(r.role === "owner" ? r : null); setTab("build"); setRunNonce((n) => n + 1); };

  // A change to the kernel's copy, said in the kernel's words when it is refused. A stale edit
  // reloads what the kernel has so the person is looking at the truth when they try again.
  const attempt = async (work) => {
    try {
      return await work();
    } catch (e) {
      if (e?.error === "version_conflict") await refresh();
      throw e;
    }
  };

  const save = (name, st) => attempt(async () => {
    await api.create({ name, kind: "report", body: reportBody(st, schema) });
    await refresh();
  });
  // Changes the saved report in place: the kernel keeps the version it replaces. Returns the new version.
  const update = (item, st) => attempt(async () => {
    const done = await api.edit(item.id, {
      expected_version: versions.current[item.id] ?? item.version,
      body: reportBody(st, schema),
    });
    versions.current[item.id] = done.report.version;
    setEditing(savedFrom(done.report));
    await refresh();
    return done.report.version;
  });
  const copy = (r) => attempt(async () => {
    await api.create({ name: `${r.name} (copy)`.slice(0, 120), kind: "report", body: reportBody(r.state, schema) });
    await refresh();
    setNotice(`Kept a copy of “${r.name}” that is yours.`);
  }).catch((e) => setNotice(failureText(e)));
  const share = (item, users, groupsOut) => attempt(async () => {
    const done = await api.share(item.id, { expected_version: versions.current[item.id] ?? item.version, users, groups: groupsOut });
    versions.current[item.id] = done.report.version;
    await refresh();
  });
  const schedule = (item, plan) => attempt(async () => {
    const done = await api.schedule(item.id, { expected_version: versions.current[item.id] ?? item.version, schedule: plan });
    versions.current[item.id] = done.report.version;
    await refresh();
    setNotice(plan ? `“${item.name}” will be emailed. ${scheduleLine(done.report.schedule)}.` : `“${item.name}” is no longer emailed.`);
  });
  const setAlerts = (item, alerts) => attempt(async () => {
    const done = await api.alerts(item.id, { expected_version: versions.current[item.id] ?? item.version, alerts });
    versions.current[item.id] = done.report.version;
    await refresh();
    setNotice(alerts.length ? `Alert set on “${item.name}”.` : `“${item.name}” has no alert any more.`);
  });
  const rollback = (report, version) => attempt(async () => {
    const done = await api.rollback(report.id, { expected_version: report.version, version });
    versions.current[report.id] = done.report.version;
    await refresh();
  });
  const confirmRemove = (item) => attempt(async () => {
    await api.remove(item.id, { expected_version: versions.current[item.id] ?? item.version });
    if (item.id === dashboardId) setDashboardId("");
    await refresh();
  });

  const createDashboard = (name) => attempt(async () => {
    const made = await api.create({
      name, kind: "dashboard", body: { items: [], filters: [], range: { from: "", to: "" } },
    });
    // Selected before the list is read again, so the page lands on it and not on the first one.
    setDashboardId(made.report.id);
    await refresh();
  }).catch((e) => setNotice(failureText(e)));

  // Edits to a dashboard go one after another, and each is made against the newest copy this page
  // has: `change` is a function from that copy to the one to save, so two quick edits (two charts
  // added in a row) do not each start from the same stale one and the second undo the first.
  const changeDashboard = (id, change) => {
    queue.current = queue.current.then(() => attempt(async () => {
      const current = latest.current[id];
      if (!current) return;
      const done = await api.edit(id, {
        expected_version: current.version,
        body: dashboardBody(change(current), schema),
      });
      versions.current[id] = done.report.version;
      latest.current[id] = dashboardFrom(done.report);
      await refresh();
    })).catch((e) => setNotice(failureText(e)));
    return queue.current;
  };

  const moveLocal = async () => {
    setMoving(true);
    let failed = 0;
    for (const r of loadSaved(owner)) {
      try {
        await api.create({ name: r.name, kind: "report", body: reportBody(r.state, schema) });
        deleteReport(owner, r.id);
      } catch { failed += 1; }
    }
    const remaining = [];
    for (const d of loadDashboards(owner)) {
      try {
        await api.create({ name: d.name, kind: "dashboard", body: dashboardBody(d, schema) });
      } catch { failed += 1; remaining.push(d); }
    }
    saveDashboards(owner, remaining);
    await refresh();
    setLocal(movable(loadSaved(owner), loadDashboards(owner)));
    setMoving(false);
    setNotice(failed ? `${failed} could not be moved and are still in this browser.` : "Moved to the kernel.");
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", px: { xs: 2, sm: 3 }, py: 3 }}>
      <Stack sx={{ gap: 2 }}>
        <Stack direction="row" sx={{ gap: 2, justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
          <Stack direction="row" sx={{ gap: 2, alignItems: "center" }}>
            <KernelLockup />
            <Typography variant="h1" component="h1">Insights</Typography>
          </Stack>
          <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
            <SchemeToggle />
            <Button size="small" color="inherit" onClick={onSignOut}>Sign out</Button>
          </Stack>
        </Stack>

        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: "70ch" }}>
          Charts over whatever your connectors brought in. Every figure counts only the records you can open,
          so a person with different access sees different numbers.
        </Typography>

        <Permission
          groups={groups}
          effective={permitted ? { permitted, declared: null } : undefined}
          gate="Insights has no ceiling of its own. It can ask about any type, and what comes back is limited only by the tags you hold: a record is counted only if every tag on it is one of yours."
        />

        {failure ? (
          <ErrorState failure={failure} title="Insights could not read the schema" />
        ) : !schema || !state ? (
          <Loading label="Reading what the kernel serves" minHeight={200} />
        ) : (
          <>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label="Insights" variant="scrollable" scrollButtons="auto">
              <Tab value="overview" label="Overview" />
              <Tab value="build" label="Build a chart" />
              <Tab value="saved" label={`Saved (${list ? saved.length : "…"})`} />
              <Tab value="dashboards" label={`Dashboards (${list ? dashboards.length : "…"})`} />
            </Tabs>
            {notice ? (
              <Alert severity="info" role="status" onClose={() => setNotice("")}>{notice}</Alert>
            ) : null}
            <Box role="tabpanel">
              {tab === "overview" ? (
                <Overview schema={schema} query={query} onOpen={openType} onAnswer={onAnswer} onCounts={setCounts} />
              ) : tab === "build" ? (
                <Build
                  schema={schema} state={state} setState={setState} query={query}
                  onAnswer={onAnswer} runNonce={runNonce} onSave={save} onUpdate={update} editing={editing} onDrill={setDrilling}
                  onAsk={askInWords} sources={sources}
                />
              ) : tab === "saved" ? (
                <Saved
                  list={saved} schema={schema} loading={list === null} failed={listFailed}
                  local={local} moving={moving} onMove={moveLocal}
                  onOpen={openSaved} onShare={setSharing} onHistory={setVersioning} onSchedule={setEmailing} onAlert={setAlerting}
                  onDelete={(r) => setRemoving({ item: r, noun: "report" })} onCopy={copy}
                />
              ) : (
                <Dashboards
                  dashboards={dashboards} declared={declared} declaredFailed={suggested === false}
                  loading={list === null} failed={listFailed} local={local} moving={moving} onMove={moveLocal}
                  selectedId={dashboardId} onSelect={setDashboardId}
                  onCreate={createDashboard} onChange={changeDashboard}
                  onShare={setSharing} onHistory={setVersioning}
                  onDelete={(d) => setRemoving({ item: d, noun: "dashboard" })}
                  saved={saved} schema={schema} query={query} onAnswer={onAnswer} onDrill={setDrilling}
                />
              )}
            </Box>
          </>
        )}
      </Stack>
      <DrillDialog request={drilling} drill={drill} schema={schema} onClose={() => setDrilling(null)} />
      {sharing ? <ShareDialog item={sharing} myGroups={groups} onClose={() => setSharing(null)} onShare={share} /> : null}
      {alerting ? <AlertDialog item={alerting} me={owner} label={figureOf(alerting, schema)} onClose={() => setAlerting(null)} onSave={setAlerts} /> : null}
      {emailing ? <ScheduleDialog item={emailing} me={owner} onClose={() => setEmailing(null)} onSchedule={schedule} /> : null}
      {versioning ? (
        <HistoryDialog
          item={versioning} load={async (id) => (await get(`/insights/reports/${id}`)).report}
          onClose={() => setVersioning(null)} onRollback={rollback}
        />
      ) : null}
      {removing ? (
        <ConfirmDialog
          item={removing.item} noun={removing.noun}
          shared={Boolean(removing.item.sharedUsers?.length || removing.item.sharedGroups?.length)}
          onClose={() => setRemoving(null)} onConfirm={confirmRemove}
        />
      ) : null}
    </Box>
  );
}
